/**
 * Ejemplos de tags BBCode personalizados
 * Demuestra cómo añadir nuevos formatos fácilmente
 */

// Ejemplo 1: Tag para resaltar texto
export const highlightTag = {
  name: 'highlight',
  config: {
    hasAttribute: true,
    attributeName: 'color',
    transform: (content, attribute) => {
      const bgColor = attribute || 'yellow'
      return `<mark style="background-color: ${bgColor}; padding: 2px 4px; border-radius: 3px;">${content}</mark>`
    },
    validate: (attribute) => {
      if (!attribute) return true
      // Validar colores básicos o hex
      return /^(yellow|green|blue|red|orange|pink|#[0-9A-Fa-f]{3,6})$/.test(attribute)
    },
    priority: 2,
  },
}

// Ejemplo 2: Tag para crear alertas/avisos
export const alertTag = {
  name: 'alert',
  config: {
    hasAttribute: true,
    attributeName: 'type',
    transform: (content, attribute) => {
      const alertType = attribute || 'info'
      const alertStyles = {
        info: 'background: #d1ecf1; border: 1px solid #bee5eb; color: #0c5460;',
        warning: 'background: #fff3cd; border: 1px solid #ffeaa7; color: #856404;',
        error: 'background: #f8d7da; border: 1px solid #f5c6cb; color: #721c24;',
        success: 'background: #d4edda; border: 1px solid #c3e6cb; color: #155724;',
      }

      const style = alertStyles[alertType] || alertStyles.info
      const icon =
        {
          info: 'ℹ️',
          warning: '⚠️',
          error: '❌',
          success: '✅',
        }[alertType] || 'ℹ️'

      return `<div class="bbcode-alert" style="${style} padding: 12px; border-radius: 4px; margin: 16px 0;">
        <strong>${icon} ${alertType.toUpperCase()}</strong><br>
        ${content}
      </div>`
    },
    validate: (attribute) => {
      if (!attribute) return true
      return ['info', 'warning', 'error', 'success'].includes(attribute)
    },
    priority: 4,
  },
}

// Ejemplo 3: Tag para crear tablas simples
export const tableTag = {
  name: 'table',
  config: {
    transform: (content) => {
      // Procesar filas separadas por |
      const rows = content
        .trim()
        .split('\n')
        .filter((row) => row.trim())
      let tableHtml = '<table class="bbcode-table" style="border-collapse: collapse; width: 100%; margin: 16px 0;">'

      rows.forEach((row, index) => {
        const cells = row.split('|').map((cell) => cell.trim())
        const tag = index === 0 ? 'th' : 'td'
        const style =
          index === 0
            ? 'background: #f8f9fa; font-weight: bold; padding: 8px; border: 1px solid #ddd;'
            : 'padding: 8px; border: 1px solid #ddd;'

        tableHtml += '<tr>'
        cells.forEach((cell) => {
          tableHtml += `<${tag} style="${style}">${cell}</${tag}>`
        })
        tableHtml += '</tr>'
      })

      tableHtml += '</table>'
      return tableHtml
    },
    priority: 6,
  },
}

// Ejemplo 4: Tag para crear acordeones/colapsables
export const accordionTag = {
  name: 'accordion',
  config: {
    hasAttribute: true,
    attributeName: 'title',
    transform: (content, attribute) => {
      const title = attribute || 'Mostrar/Ocultar'
      return `<details class="bbcode-accordion" style="margin: 16px 0; border: 1px solid #ddd; border-radius: 4px;">
        <summary style="padding: 12px; background: #f8f9fa; cursor: pointer; font-weight: bold;">${title}</summary>
        <div style="padding: 12px;">${content}</div>
      </details>`
    },
    priority: 5,
  },
}

// Ejemplo 5: Tag para crear badges/etiquetas
export const badgeTag = {
  name: 'badge',
  config: {
    hasAttribute: true,
    attributeName: 'color',
    transform: (content, attribute) => {
      const color = attribute || 'primary'
      const badgeStyles = {
        primary: 'background: #007bff; color: white;',
        secondary: 'background: #6c757d; color: white;',
        success: 'background: #28a745; color: white;',
        danger: 'background: #dc3545; color: white;',
        warning: 'background: #ffc107; color: black;',
        info: 'background: #17a2b8; color: white;',
        light: 'background: #f8f9fa; color: black; border: 1px solid #dee2e6;',
        dark: 'background: #343a40; color: white;',
      }

      const style = badgeStyles[color] || badgeStyles.primary
      return `<span class="bbcode-badge" style="${style} padding: 4px 8px; border-radius: 12px; font-size: 0.8em; font-weight: bold; display: inline-block;">${content}</span>`
    },
    validate: (attribute) => {
      if (!attribute) return true
      return ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'].includes(attribute)
    },
    priority: 2,
  },
}

// Ejemplo 6: Tag para crear progress bars
export const progressTag = {
  name: 'progress',
  config: {
    hasAttribute: true,
    attributeName: 'value',
    transform: (content, attribute) => {
      const value = Math.min(100, Math.max(0, parseInt(attribute) || 0))
      const label = content || `${value}%`
      return `<div class="bbcode-progress" style="background: #e9ecef; border-radius: 4px; overflow: hidden; margin: 8px 0;">
        <div style="background: #007bff; color: white; padding: 4px 8px; width: ${value}%; text-align: center; font-size: 0.9em; font-weight: bold; min-width: fit-content;">${label}</div>
      </div>`
    },
    validate: (attribute) => {
      if (!attribute) return false
      const num = parseInt(attribute)
      return !isNaN(num) && num >= 0 && num <= 100
    },
    priority: 3,
  },
}

// Ejemplo 7: Tag para crear tooltips
export const tooltipTag = {
  name: 'tooltip',
  config: {
    hasAttribute: true,
    attributeName: 'text',
    transform: (content, attribute) => {
      const tooltipText = attribute || 'Tooltip'
      return `<span class="bbcode-tooltip" style="position: relative; cursor: help; border-bottom: 1px dotted #999;" title="${tooltipText}">${content}</span>`
    },
    priority: 1,
  },
}

// Ejemplo de cómo usar estos tags personalizados:
export const exampleUsage = `
// Para usar estos tags personalizados en tu BBCodeEditor:

import BBCodeEditor from './components/BBCodeEditor/BBCodeEditor';
import { highlightTag, alertTag, tableTag, accordionTag, badgeTag, progressTag, tooltipTag } from './libraries/BBCode/CustomTags';

const customTags = [
  highlightTag,
  alertTag,
  tableTag,
  accordionTag,
  badgeTag,
  progressTag,
  tooltipTag
];

function MyComponent() {
  const [content, setContent] = useState('');
  
  return (
    <BBCodeEditor
      value={content}
      onChange={setContent}
      customTags={customTags}
      height="400px"
    />
  );
}

// Ejemplos de uso en BBCode:
// [highlight=yellow]Texto resaltado[/highlight]
// [alert=warning]¡Cuidado con esto![/alert]
// [table]
// Nombre | Edad | Ciudad
// Juan | 25 | Madrid
// María | 30 | Barcelona
// [/table]
// [accordion=Más información]Contenido oculto aquí[/accordion]
// [badge=success]Completado[/badge]
// [progress=75]Progreso del proyecto[/progress]
// [tooltip=Información adicional]Texto con tooltip[/tooltip]
`
